import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Card,
  CardProps,
  Checkbox,
  Divider,
  Input,
  Switch,
  SwitchProps,
  Tag,
  TagProps,
  Tooltip,
  Typography,
} from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import { AlwaysVisibleRegistrationFieldType, RegistrationFieldType } from '../../types';
import Form from '../Form';
import styles from './RegistrationTemplateForm.module.css';

export interface RegistrationTemplateFormValues {
  name: string;
  linkedinOrResume: AlwaysVisibleRegistrationFieldType;
  linkedin: AlwaysVisibleRegistrationFieldType;
  resume: AlwaysVisibleRegistrationFieldType;
  cpf: RegistrationFieldType;
  pcd: RegistrationFieldType;
  cid: RegistrationFieldType;
  pcdReport: RegistrationFieldType;
  birthday: RegistrationFieldType;
  salary: RegistrationFieldType;
  address: RegistrationFieldType;
  relocation: RegistrationFieldType;
  travel: RegistrationFieldType;
  portfolio: RegistrationFieldType;
  defaultTemplate: boolean;
}

const FixedFormFieldItemCard = ({ title, ...props }: CardProps) => {
  return (
    <Form.Col span={24}>
      <Card type="inner" {...props}>
        <Form.Row justify="space-between">
          <Form.Col>{title}</Form.Col>
          <Form.Col>
            <Switch checked disabled />
          </Form.Col>
        </Form.Row>
      </Card>
    </Form.Col>
  );
};

interface AlwaysVisibleFormFieldItemCardProps extends CardProps {
  formItemName: string;
  onChangeSwitchSideEffect?: (checked: boolean) => void;
}

const AlwaysVisibleFormFieldItemCard = ({
  title,
  formItemName,
  onChangeSwitchSideEffect,
  ...props
}: AlwaysVisibleFormFieldItemCardProps) => {
  const form = Form.useFormInstance();
  const fieldValue = Form.useWatch<AlwaysVisibleRegistrationFieldType>(formItemName, form);

  return (
    <Form.Col span={24}>
      <Card type="inner" {...props}>
        <Form.Row justify="space-between">
          <Form.Col>{title}</Form.Col>
          <Form.Col>
            <Form.Item name={formItemName} noStyle>
              <Switch
                checked={fieldValue === 'REQUIRED'}
                onChange={(checked) => {
                  form.setFieldValue(formItemName, checked ? 'REQUIRED' : 'NOT_REQUIRED');
                  onChangeSwitchSideEffect?.(checked);
                }}
              />
            </Form.Item>
          </Form.Col>
        </Form.Row>
      </Card>
    </Form.Col>
  );
};

interface FormFieldItemHeaderProps {
  formItemName: string;
  onClose: () => void;
  title?: string | React.ReactNode;
  switchProps?: SwitchProps;
}

const FormFieldItemHeader = ({
  title,
  formItemName,
  onClose,
  switchProps,
}: FormFieldItemHeaderProps) => {
  return (
    <Form.Row>
      <Form.Col>
        <CloseOutlined onClick={onClose} />
      </Form.Col>
      <Form.Col>{title}</Form.Col>
      <Form.Col flex={1} />
      <Form.Col>
        <Form.Item name={formItemName} noStyle>
          <Switch {...switchProps} />
        </Form.Item>
      </Form.Col>
    </Form.Row>
  );
};

interface ActiveFormFieldItemCardProps extends CardProps {
  formItemName: string;
  onRemoveField?: () => void;
}

const ActiveFormFieldItemCard = ({
  title,
  formItemName,
  onRemoveField,
  ...props
}: ActiveFormFieldItemCardProps) => {
  const form = Form.useFormInstance();
  const fieldValue = Form.useWatch<RegistrationFieldType>(formItemName, form);

  return (
    <Form.Col span={24} hidden={fieldValue === 'NOT_VISIBLE'}>
      <Card type="inner" {...props}>
        <FormFieldItemHeader
          title={title}
          formItemName={formItemName}
          onClose={() => {
            form.setFieldValue(formItemName, 'NOT_VISIBLE');
            onRemoveField?.();
          }}
          switchProps={{
            checked: fieldValue === 'VISIBLE_AND_REQUIRED',
            onChange: (checked) =>
              checked
                ? form.setFieldValue(formItemName, 'VISIBLE_AND_REQUIRED')
                : form.setFieldValue(formItemName, 'VISIBLE'),
          }}
        />
      </Card>
    </Form.Col>
  );
};

interface PcdFormItemCardProps extends CardProps {
  onRemoveField?: () => void;
}

const PcdFormItemCard = ({ onRemoveField, ...props }: PcdFormItemCardProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const pcdValue = Form.useWatch<RegistrationFieldType>('pcd', form);
  const cidValue = Form.useWatch<RegistrationFieldType>('cid', form);
  const pcdReportValue = Form.useWatch<RegistrationFieldType>('pcdReport', form);

  const cidOrPcdReportVisible = cidValue !== 'NOT_VISIBLE' || pcdReportValue !== 'NOT_VISIBLE';

  return (
    <>
      {cidOrPcdReportVisible ? (
        <Form.Col span={24} hidden={pcdValue === 'NOT_VISIBLE'}>
          <Card
            title={
              <FormFieldItemHeader
                title={t('É PcD?')}
                formItemName="pcd"
                onClose={() => {
                  form.setFieldValue('pcd', 'NOT_VISIBLE');
                  form.setFieldValue('cid', 'NOT_VISIBLE');
                  form.setFieldValue('pcdReport', 'NOT_VISIBLE');
                  onRemoveField?.();
                }}
                switchProps={{
                  checked: pcdValue === 'VISIBLE_AND_REQUIRED',
                  onChange: (checked) =>
                    checked
                      ? form.setFieldValue('pcd', 'VISIBLE_AND_REQUIRED')
                      : form.setFieldValue('pcd', 'VISIBLE'),
                }}
              />
            }
            type="inner"
            {...props}
          >
            <Form.Row gutter={[8, 16]}>
              <Form.Col span={24} hidden={cidValue === 'NOT_VISIBLE'}>
                <FormFieldItemHeader
                  title={t('Inserir CID')}
                  formItemName="cid"
                  onClose={() => {
                    form.setFieldValue('cid', 'NOT_VISIBLE');
                    onRemoveField?.();
                  }}
                  switchProps={{
                    checked: cidValue === 'VISIBLE_AND_REQUIRED',
                    onChange: (checked) =>
                      checked
                        ? form.setFieldValue('cid', 'VISIBLE_AND_REQUIRED')
                        : form.setFieldValue('cid', 'VISIBLE'),
                  }}
                />
              </Form.Col>
              <Form.Col span={24} hidden={pcdReportValue === 'NOT_VISIBLE'}>
                <FormFieldItemHeader
                  title={t('Anexar laudo')}
                  formItemName="pcdReport"
                  onClose={() => {
                    form.setFieldValue('pcdReport', 'NOT_VISIBLE');
                    onRemoveField?.();
                  }}
                  switchProps={{
                    disabled: true,
                  }}
                />
              </Form.Col>
            </Form.Row>
          </Card>
        </Form.Col>
      ) : (
        <>
          <ActiveFormFieldItemCard
            title={t('É PcD?')}
            formItemName="pcd"
            onRemoveField={onRemoveField}
          />
        </>
      )}
    </>
  );
};

interface AddVisibleFieldTagProps extends TagProps {
  formItemName: string;
  disabled?: boolean;
  disabledTooltipMessage?: string;
}

const AddVisibleFieldTag = ({
  disabled,
  formItemName,
  disabledTooltipMessage,
  onClick,
  ...props
}: AddVisibleFieldTagProps) => {
  const form = Form.useFormInstance();
  const fieldValue = Form.useWatch<RegistrationFieldType>(formItemName, form);

  return (
    <Form.Item name={formItemName} noStyle>
      <Tooltip title={disabled && disabledTooltipMessage}>
        <Tag
          className={!disabled ? styles.addVisibleFieldTag : styles.disabledAddVisibleFieldTag}
          onClick={(e) => {
            if (!disabled) {
              form.setFieldValue(formItemName, 'VISIBLE');
              onClick?.(e);
            }
          }}
          hidden={fieldValue !== 'NOT_VISIBLE'}
          {...props}
        />
      </Tooltip>
    </Form.Item>
  );
};

interface RegistrationTemplateFormProps {
  isVixtingActive?: boolean;
  isSankhyaActive?: boolean;
  disableDefaultTemplateCheckbox?: boolean;
  disableNameField?: boolean;
  disableDefaultTemplateField?: boolean;
  onAddField?: () => void;
  onRemoveField?: () => void;
}

const RegistrationTemplateForm = ({
  isSankhyaActive,
  isVixtingActive,
  disableDefaultTemplateCheckbox,
  disableNameField,
  disableDefaultTemplateField,
  onAddField,
  onRemoveField,
}: RegistrationTemplateFormProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const pcdValue = Form.useWatch<RegistrationFieldType>('pcd', form);
  const alwaysRequiredCpf = isSankhyaActive || isVixtingActive;

  return (
    <>
      <Typography.Paragraph>
        <Trans>Configure os campos que serão exibidos no formulário de inscrição</Trans>{' '}
        <Tooltip
          title={t(
            'Apenas os campos ativos, marcados com preenchimento obrigatório, serão exibidos no formulário de inscrição por WhatsApp'
          )}
        >
          <InfoCircleOutlined />
        </Tooltip>
      </Typography.Paragraph>
      {!disableNameField && (
        <Form.Item label={t('Nome do modelo')} name="name" rules={[{ required: true }]}>
          <Input placeholder={t('Ex: Formulário Padrão')} />
        </Form.Item>
      )}
      {!disableDefaultTemplateField && (
        <Form.Item name="defaultTemplate" valuePropName="checked">
          <Checkbox disabled={disableDefaultTemplateCheckbox}>
            <Trans>Usar como modelo padrão de formulário</Trans>
          </Checkbox>
        </Form.Item>
      )}
      <Form.Row gutter={[8, 8]}>
        <Divider />
        <Form.Col span={24}>
          <Form.Legend>
            <Trans>Campos disponíveis</Trans>
          </Form.Legend>
        </Form.Col>
        {!alwaysRequiredCpf && (
          <AddVisibleFieldTag formItemName="cpf" onClick={onAddField}>
            <Trans>CPF</Trans>
          </AddVisibleFieldTag>
        )}
        <AddVisibleFieldTag formItemName="portfolio" onClick={onAddField}>
          <Trans>Portfólio / Github / Site</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag formItemName="pcd" onClick={onAddField}>
          <Trans>É PcD?</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag
          onClick={onAddField}
          formItemName="cid"
          disabled={pcdValue === 'NOT_VISIBLE'}
          disabledTooltipMessage={t('Habilite o campo "É PcD?" para habilitar este campo')}
        >
          <Trans>Inserir CID</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag
          onClick={onAddField}
          formItemName="pcdReport"
          disabled={pcdValue === 'NOT_VISIBLE'}
          disabledTooltipMessage={t('Habilite o campo "É PcD?" para habilitar este campo')}
        >
          <Trans>Anexar laudo</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag formItemName="birthday" onClick={onAddField}>
          <Trans>Data de Nascimento</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag formItemName="salary" onClick={onAddField}>
          <Trans>Pretensão salarial</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag formItemName="address" onClick={onAddField}>
          <Trans>Endereço (país, estado e cidade)</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag formItemName="relocation" onClick={onAddField}>
          <Trans>Possibilidade de mudança?</Trans>
        </AddVisibleFieldTag>
        <AddVisibleFieldTag formItemName="travel" onClick={onAddField}>
          <Trans>Aceita viajar pela empresa?</Trans>
        </AddVisibleFieldTag>
        <Divider />
        <Form.Col span={24}>
          <Form.Row justify="space-between">
            <Form.Col>
              <Form.Legend>
                <Trans>Campos ativos</Trans>
              </Form.Legend>
            </Form.Col>
            <Form.Col>
              <Form.Legend>
                <Trans>Preenchimento obrigatório</Trans>
              </Form.Legend>
            </Form.Col>
          </Form.Row>
        </Form.Col>
        <Form.Row gutter={[8, 8]}>
          <FixedFormFieldItemCard title={t('Nome completo')} />
          <FixedFormFieldItemCard title={t('Email')} />
          <FixedFormFieldItemCard title={t('Telefone')} />
          <FixedFormFieldItemCard title={t('Como ficou sabendo da vaga?')} />
          <AlwaysVisibleFormFieldItemCard
            title={t('LinkedIn ou Currículo')}
            formItemName="linkedinOrResume"
            onChangeSwitchSideEffect={() => {
              form.setFieldValue('linkedin', 'NOT_REQUIRED');
              form.setFieldValue('resume', 'NOT_REQUIRED');
            }}
          />
          <AlwaysVisibleFormFieldItemCard
            title="LinkedIn"
            formItemName="linkedin"
            onChangeSwitchSideEffect={() => form.setFieldValue('linkedinOrResume', 'NOT_REQUIRED')}
          />
          <AlwaysVisibleFormFieldItemCard
            title={t('Currículo')}
            formItemName="resume"
            onChangeSwitchSideEffect={() => form.setFieldValue('linkedinOrResume', 'NOT_REQUIRED')}
          />
          {alwaysRequiredCpf ? (
            <FixedFormFieldItemCard
              title={
                <Typography.Text>
                  <Trans>CPF</Trans>{' '}
                  <Tooltip
                    title={
                      <Typography.Text style={{ color: 'white' }}>
                        {isVixtingActive ? (
                          <Trans>Você está utilizando a integração com o Vlow da Vixting.</Trans>
                        ) : (
                          <Trans>
                            Você está utilizando a integração com a Admissão Digital da Sankhya.
                          </Trans>
                        )}
                        <br />
                        <Trans>
                          Nesse caso, o campo{' '}
                          <Typography.Text style={{ color: 'white' }} strong>
                            CPF
                          </Typography.Text>{' '}
                          não pode ser alterado, e sempre é{' '}
                          <Typography.Text style={{ color: 'white' }} strong>
                            obrigatório
                          </Typography.Text>
                          .
                        </Trans>
                      </Typography.Text>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Typography.Text>
              }
            />
          ) : (
            <ActiveFormFieldItemCard
              title={t('CPF')}
              formItemName="cpf"
              onRemoveField={onRemoveField}
            />
          )}
          <ActiveFormFieldItemCard
            title={t('Portfólio / Github / Site')}
            formItemName="portfolio"
            onRemoveField={onRemoveField}
          />
          <PcdFormItemCard onRemoveField={onRemoveField} />
          <ActiveFormFieldItemCard
            title={t('Data de Nascimento')}
            formItemName="birthday"
            onRemoveField={onRemoveField}
          />
          <ActiveFormFieldItemCard
            title={t('Pretensão salarial')}
            formItemName="salary"
            onRemoveField={onRemoveField}
          />
          <ActiveFormFieldItemCard
            title={t('Endereço (país, estado e cidade)')}
            formItemName="address"
            onRemoveField={onRemoveField}
          />
          <ActiveFormFieldItemCard
            title={t('Possibilidade de mudança?')}
            formItemName="relocation"
            onRemoveField={onRemoveField}
          />
          <ActiveFormFieldItemCard
            title={t('Aceita viajar pela empresa?')}
            formItemName="travel"
            onRemoveField={onRemoveField}
          />
        </Form.Row>
      </Form.Row>
    </>
  );
};

export default RegistrationTemplateForm;
